import { AutomationTrip, AutomationTripAssignmentReason } from '@/models/gen/graphql';
import { ListGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import { ReactElement, forwardRef, useState } from 'react';

import Button from '@/common/Button';
import { Dayts } from '@/utils';
import Table from '@/common/Table';
import { TableRowProps } from '@/common/Table/TableRow';
import { searchAutomationTripAssignmentReason } from '@/features/Automation/api/services/searchAutomationTrip';
import { useAutomationTrips } from '@/features/Automation/pages/AutomationTrips';

export type AutomationTripsTableRowProps = TableRowProps & Partial<AutomationTrip>;

const AutomationTripsTableRow = ({
  airportCode,
  attendants,
  doLocationName,
  driverFullName,
  newDriverFullName,
  flightNumber,
  id,
  loopName,
  payerProviderDisplayName,
  pilots,
  puLocationName,
  scheduled,
  servicerIataAirlineCode,
  type,
  vehicleTrackingId,
}: AutomationTripsTableRowProps): ReactElement => {
  const [{ isPreview }] = useAutomationTrips(({ state }) => [{ isPreview: state.filters.isPreview }]);

  return (
    <Table.Row key={id}>
      <Table.Cell data-placeholder="--">{type ?? null}</Table.Cell>
      <Table.Cell data-placeholder="--">{scheduled ? new Dayts(scheduled).format('MM/DD') : null}</Table.Cell>
      <Table.Cell data-placeholder="--">{scheduled ? new Dayts(scheduled).format('HH:mm') : null}</Table.Cell>
      <Table.Cell data-placeholder="--">{airportCode ?? null}</Table.Cell>
      <Table.Cell data-placeholder="--">{servicerIataAirlineCode ?? null}</Table.Cell>
      <Table.Cell data-placeholder="--">{loopName || flightNumber ? flightNumber?.toString().padStart(4, '0') : null}</Table.Cell>
      <Table.Cell data-placeholder="--">{pilots ?? null}</Table.Cell>
      <Table.Cell data-placeholder="--">{attendants ?? null}</Table.Cell>
      <Table.Cell data-placeholder="--">{(isPreview ? driverFullName : newDriverFullName) ?? null}</Table.Cell>
      <TripStatusCell id={!newDriverFullName ? (isPreview ? false : id) : undefined} />
      <Table.Cell data-placeholder="--">{vehicleTrackingId ?? null}</Table.Cell>
      <Table.Cell data-placeholder="--">{puLocationName ?? null}</Table.Cell>
      <Table.Cell data-placeholder="--">{doLocationName ?? null}</Table.Cell>
      <Table.Cell data-placeholder="--">{payerProviderDisplayName ?? null}</Table.Cell>
    </Table.Row>
  );
};

const TripStatus = ({ id }: { id?: string | false }, ref): ReactElement => {
  const [reasons, setReasons] = useState<AutomationTripAssignmentReason[]>();
  const [loading, setLoading] = useState(false);

  const getReasons = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await searchAutomationTripAssignmentReason(id);
      setReasons(res);
    } catch (error) {
      console.error('Failed to fetch reasons:', error);
    } finally {
      setLoading(false);
    }
  };
  const onToggle = (isOpen: boolean) => {
    if (!isOpen) return;
    getReasons();
  };

  return (
    <Table.Cell ref={ref}>
      {id === undefined && <i className="sv sv-power text-green" />}
      {id === false && <i className="sv sv-power opacity-25" />}
      {id && (
        <OverlayTrigger
          trigger="click"
          rootClose
          onToggle={onToggle}
          overlay={
            <Popover id={`popover-${id}`}>
              <Popover.Body style={{ maxWidth: 300, maxHeight: 300, overflowY: 'auto' }}>
                {loading && <i className="fa fa-spinner fa-pulse" />}
                {!loading && (
                  <ListGroup variant="flush">
                    {!reasons?.length && (
                      <ListGroup.Item>
                        <h6 className="text-danger">Unknown Reason</h6>
                        <span>Something went wrong during automation. Please try again.</span>
                      </ListGroup.Item>
                    )}
                    {reasons?.length > 0 &&
                      reasons.map((reason) => (
                        <ListGroup.Item key={reason.id} className="d-flex flex-column">
                          <h6 className="text-danger">{reason.title ?? 'Unknown Reason'}</h6>
                          <span dangerouslySetInnerHTML={{ __html: reason.displayMessage ?? 'No description available' }} />
                        </ListGroup.Item>
                      ))}
                  </ListGroup>
                )}
              </Popover.Body>
            </Popover>
          }
        >
          <Button variant="icon">
            <i className="sv sv-warning text-red" />
          </Button>
        </OverlayTrigger>
      )}
    </Table.Cell>
  );
};
const TripStatusCell = forwardRef(TripStatus);

export default AutomationTripsTableRow;
